import type { NetMessage, PeerId } from './transport';
import { reconnectRegistry } from './reconnect';

export type Provenance =
  | { kind: 'internal' }
  | { kind: 'host-only' }
  | { kind: 'client' }
  | { kind: 'owner'; field: string };

/**
 * Single source of truth for who is allowed to send each message type.
 * A type missing here defaults to the strictest rule (host-only) and is warned
 * about in dev — so any new message must be registered before it can flow.
 */
export const MESSAGE_PROVENANCE: Record<string, Provenance> = {
  'clock-ping': { kind: 'internal' },
  'clock-pong': { kind: 'internal' },

  snapshot: { kind: 'host-only' },
  floor: { kind: 'host-only' },
  'projectile-spawn': { kind: 'host-only' },
  'projectile-despawn': { kind: 'host-only' },
  vfx: { kind: 'host-only' },
  'vfx-despawn': { kind: 'host-only' },
  'preview-add': { kind: 'host-only' },
  'preview-remove': { kind: 'host-only' },
  'cast-rejected': { kind: 'host-only' },
  'damage-number': { kind: 'host-only' },
  'hub-roster': { kind: 'host-only' },
  'run-start': { kind: 'host-only' },
  'enter-dungeon': { kind: 'host-only' },
  'floor-transition-start': { kind: 'host-only' },
  'return-to-hub': { kind: 'host-only' },
  'session-closed': { kind: 'host-only' },
  'world-update': { kind: 'host-only' },
  'monster-killed': { kind: 'host-only' },
  'location-fire': { kind: 'host-only' },
  'player-died': { kind: 'host-only' },
  'player-revived': { kind: 'host-only' },
  'run-wipe': { kind: 'host-only' },
  'common-item-add': { kind: 'host-only' },
  'common-item-remove': { kind: 'host-only' },

  'death-resolution': { kind: 'owner', field: 'playerId' },

  'move-intent': { kind: 'client' },
  'face-intent': { kind: 'client' },
  'attack-intent': { kind: 'client' },
  'skill-intent': { kind: 'client' },
  'interact-intent': { kind: 'client' },
  'revive-request': { kind: 'client' },
  'request-floor': { kind: 'client' },
  'hub-presence': { kind: 'client' },
  'player-loadout': { kind: 'client' },

  'reconnect-request': { kind: 'client' },
  'reconnect-accept': { kind: 'host-only' },
  'reconnect-reject': { kind: 'host-only' },
  'run-ended': { kind: 'host-only' },

  'leave-run': { kind: 'client' },
  'throw-item': { kind: 'client' },
  'common-pickup-request': { kind: 'client' },
};

export interface ProvenanceResult {
  ok: boolean;
  /** True when the type was not declared (defaulted to host-only). */
  undeclared?: boolean;
}

export function checkProvenance(
  msg: NetMessage,
  from: PeerId,
  hostPeerId: PeerId,
): ProvenanceResult {
  const prov = MESSAGE_PROVENANCE[msg.type];
  if (!prov) {
    return { ok: from === hostPeerId, undeclared: true };
  }
  switch (prov.kind) {
    case 'internal':
      return { ok: true };
    case 'host-only':
      return { ok: from === hostPeerId };
    case 'client':
      return { ok: from !== hostPeerId && from !== '' };
    case 'owner':
      // Resolve the sender's (possibly post-reconnect) peerId to its avatar id
      // before matching the owned field — a reconnected peer sends from a NEW
      // peerId while the field still holds its original avatar id. Same mapping
      // every host-side handler uses (cf. resolvePlayerId).
      return {
        ok:
          from === hostPeerId ||
          (msg as Record<string, unknown>)[prov.field] ===
            reconnectRegistry.resolvePlayerId(from),
      };
  }
}
